import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import MockLiveMonitor from '../../components/MockLiveMonitor';

// ─── Color constants ────────────────────────────────────────────────────────
const C = {
  navy:   '#0D1B2A',
  teal:   '#00B4D8',
  success:'#10B981',
  error:  '#EF4444',
  grey:   '#6B7280',
  border: '#E5E7EB',
  bg:     '#F0F7FA',
  amber:  '#F59E0B',
};

// ─── Tiny helpers ────────────────────────────────────────────────────────────
const fmt = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};
const fmtTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
};
const fmtDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ─── Badge ───────────────────────────────────────────────────────────────────
const Badge = ({ label, color, bg }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    color,
    background: bg,
    letterSpacing: 0.3,
  }}>
    {label}
  </span>
);

const paymentBadge = (status) => {
  const map = {
    pending:  { label: 'Pending',  color: '#92400E', bg: '#FEF3C7' },
    paid:     { label: 'Paid',     color: '#065F46', bg: '#D1FAE5' },
    refunded: { label: 'Refunded', color: C.grey,    bg: '#F3F4F6' },
  };
  const s = map[status] || { label: status, color: C.grey, bg: '#F3F4F6' };
  return <Badge {...s} />;
};

const testStatusBadge = (status) => {
  const map = {
    booked:    { label: 'Booked',    color: C.grey,    bg: '#F3F4F6' },
    activated: { label: 'Activated', color: '#1D4ED8', bg: '#DBEAFE' },
    completed: { label: 'Completed', color: '#065F46', bg: '#D1FAE5' },
  };
  const s = map[status] || { label: status, color: C.grey, bg: '#F3F4F6' };
  return <Badge {...s} />;
};

const resultStatusBadge = (status) => {
  const map = {
    submitted: { label: 'Awaiting Mark', color: '#92400E', bg: '#FEF3C7' },
    marked:    { label: 'Marked',        color: '#065F46', bg: '#D1FAE5' },
  };
  const s = map[status] || { label: status, color: C.grey, bg: '#F3F4F6' };
  return <Badge {...s} />;
};

// ─── Modal ───────────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(13,27,42,0.55)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{
      background: '#fff',
      borderRadius: 16,
      boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      width: '100%', maxWidth: 480,
      padding: 32,
      maxHeight: '90vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.navy }}>{title}</h3>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 22, color: C.grey, lineHeight: 1,
        }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// ─── Form field ──────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 6 }}>
      {label}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  fontSize: 14,
  color: C.navy,
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle = { ...inputStyle, background: '#fff' };

// ─── Button helpers ──────────────────────────────────────────────────────────
const Btn = ({ children, onClick, color = C.teal, textColor = '#fff', disabled = false, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: disabled ? '#D1D5DB' : color,
      color: disabled ? '#9CA3AF' : textColor,
      border: 'none',
      borderRadius: 8,
      padding: '8px 16px',
      fontSize: 13,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'opacity .15s',
      ...style,
    }}
  >
    {children}
  </button>
);

const GhostBtn = ({ children, onClick, style = {} }) => (
  <button onClick={onClick} style={{
    background: 'none',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: C.grey,
    cursor: 'pointer',
    ...style,
  }}>
    {children}
  </button>
);

// ─── Table primitives ────────────────────────────────────────────────────────
const TH = ({ children, style = {} }) => (
  <th style={{
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 700,
    color: C.grey,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: `1px solid ${C.border}`,
    background: '#FAFAFA',
    ...style,
  }}>{children}</th>
);

const TD = ({ children, style = {} }) => (
  <td style={{
    padding: '11px 14px',
    fontSize: 14,
    color: C.navy,
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: 'middle',
    ...style,
  }}>{children}</td>
);

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: BOOKINGS
// ═══════════════════════════════════════════════════════════════════════════════
const BOOKING_FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending Payment' },
  { key: 'paid',      label: 'Paid' },
  { key: 'activated', label: 'Activated' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

// Build a printable, paper-based exam from a mock test (question paper +
// invigilator listening script + answer key). Opens in a new window to print.
function buildPaperHtml(data) {
  const esc = (x) => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const nl = (x) => esc(x).replace(/\n/g, '<br>');
  const tt = data.test_type || {}; const qs = data.questions || [];
  const SEC = { listening:'Listening', reading:'Reading', writing:'Writing', speaking:'Speaking', grammar:'Grammar', vocabulary:'Vocabulary', math_no_calc:'Math (No Calculator)', math_calc:'Math (Calculator)', reading_writing:'Reading & Writing', mathematics:'Mathematics', critical_thinking:'Critical Thinking', english:'English' };
  const INSTR = {
    listening: 'How this works: your tutor will read the recording ALOUD ONCE only — it is NOT repeated. Before each recording you get a moment to read the questions. Listen carefully and write your answers as you hear them. Spelling and numbers must be correct. At the end you have a short time to check your answers.',
    reading: 'Read the passage(s) and answer the questions. Where a gap is shown, write ONE word and/or a number.',
    writing: 'Write your response in the space provided. Manage your time carefully.',
    speaking: 'These prompts are discussed live with your examiner — nothing to write here.',
  };
  const order = []; const bySec = {};
  qs.forEach(q => { if (!bySec[q.section]) { bySec[q.section] = []; order.push(q.section); } bySec[q.section].push(q); });
  // Per-section time — same rules as the computer test: IELTS uses real centre times,
  // other tests split their total evenly across sections; speaking is live.
  const slug = (tt.slug || '').toLowerCase();
  const IELTS_MIN = { listening: 30, reading: 60, writing: 60, speaking: 0 };
  const nonSpeaking = order.filter(k => k !== 'speaking');
  const splitMin = nonSpeaking.length ? Math.max(5, Math.round((tt.duration_minutes || 0) / nonSpeaking.length)) : (tt.duration_minutes || 0);
  const minsFor = (k) => k === 'speaking' ? 0 : ((slug === 'ielts' && IELTS_MIN[k] != null) ? IELTS_MIN[k] : splitMin);
  let n = 0; const key = []; const scripts = []; let body = '';
  order.forEach(secKey => {
    const _m = minsFor(secKey);
    const _tl = secKey === 'speaking' ? ' · live, with examiner' : (_m ? ` · ${_m} minutes` : '');
    body += `<h2 class="sec">${esc(SEC[secKey] || secKey)}<span class="time">${_tl}</span></h2>`;
    if (INSTR[secKey]) body += `<p class="instr">${esc(INSTR[secKey])}</p>`;
    let lastP = null;
    bySec[secKey].forEach(q => {
      if (q.passage_text && q.passage_text !== lastP) {
        lastP = q.passage_text;
        if (secKey === 'reading') body += `<div class="passage">${nl(q.passage_text)}</div>`;
        else if (secKey === 'listening' && !scripts.includes(q.passage_text)) scripts.push(q.passage_text);
      }
      n += 1;
      const mcq = (q.question_type === 'mcq' || q.question_type === 'tfng') && (q.option_a || q.option_b);
      body += `<div class="q${q.question_type === 'essay' ? ' essay' : ''}"><span class="qn">${n}.</span> <span>${esc(q.question_text)}</span>`;
      if (q.image_url) body += `<div><img src="${esc(q.image_url)}" style="max-width:100%;margin-top:6px"></div>`;
      if (q.question_type === 'tfng' && !mcq) { body += `<div class="opt">◯ TRUE&nbsp;&nbsp;&nbsp;◯ FALSE&nbsp;&nbsp;&nbsp;◯ NOT GIVEN</div>`; key.push([n, q.correct_answer]); }
      else if (mcq) { ['a','b','c','d'].forEach(L => { const v = q['option_' + L]; if (v) body += `<div class="opt">◯ ${L.toUpperCase()}) ${esc(v)}</div>`; }); key.push([n, q.correct_answer]); }
      else if (q.question_type === 'essay') body += `<div class="lines">${'<div class="line"></div>'.repeat(26)}</div>`;
      else if (q.question_type === 'short_answer') body += `<div class="lines">${'<div class="line"></div>'.repeat(3)}</div>`;
      else if (secKey === 'speaking' || q.question_type === 'speaking') body += `<div class="speak">🎤 Discussed live with the examiner.</div>`;
      else { body += `<div class="ansrow">Answer: <span class="blank"></span></div>`; key.push([n, q.correct_answer]); }
      body += `</div>`;
    });
  });
  let scriptPage = '';
  if (scripts.length) {
    scriptPage = `<div class="pb"></div><div class="tutorhdr">TUTOR / INVIGILATOR COPY — CONFIDENTIAL</div><h2 class="sec">Invigilator Listening Script</h2>`
      + `<div class="adminnote"><strong>How to run the Listening section (${minsFor('listening') || 30} min):</strong>`
      + `<ol><li>Tell candidates the Listening section is starting and to turn to those questions.</li>`
      + `<li>Give them ~30 seconds to read the questions before each recording.</li>`
      + `<li>Read each recording below ALOUD, clearly and at a natural pace, ONCE only — do not repeat.</li>`
      + `<li>After the last recording, allow a few minutes for candidates to check answers, then move on.</li></ol></div>`;
    scripts.forEach((t, i) => { scriptPage += `<div class="passage"><strong>Recording ${i + 1}</strong><br>${nl(t)}</div>`; });
  }
  let keyPage = `<div class="pb"></div><div class="tutorhdr">TUTOR / INVIGILATOR COPY — CONFIDENTIAL</div><h2 class="sec">Answer Key — for tutor marking</h2><div class="keygrid">`;
  key.forEach(([num, ans]) => { keyPage += `<div class="keyitem"><strong>${num}.</strong> ${esc(ans)}</div>`; });
  keyPage += `</div><p class="instr">Writing and Speaking are marked by the tutor against the band descriptors below.</p>`;

  // Tutor marking guide — how to turn answers into a band/score (matches the platform).
  let guide = `<div class="pb"></div><div class="tutorhdr">TUTOR / INVIGILATOR COPY — CONFIDENTIAL</div><h2 class="sec">Tutor Marking Guide</h2>`;
  if (slug === 'ielts') {
    guide += `<p class="instr">Listening &amp; Reading: count correct answers, work out the percentage (correct ÷ total × 100), then read off the band. Writing &amp; Speaking: score each criterion 0–9 and average them. Overall band = average of the four section bands, rounded to the nearest 0.5.</p>`;
    {
      const lT = (bySec['listening'] || []).length, rT = (bySec['reading'] || []).length;
      const TH = [90, 80, 70, 60, 50, 40], BD = ['9.0', '8.0', '7.0', '6.0', '5.0', '4.0'];
      const rng = (T, i) => { if (!T) return '—'; const lo = Math.ceil(TH[i] * T / 100); const hi = (i === 0) ? T : Math.ceil(TH[i - 1] * T / 100) - 1; return lo >= hi ? `${Math.min(lo, T)}` : `${lo}–${hi}`; };
      const below = (T) => { if (!T) return '—'; return `0–${Math.max(0, Math.ceil(40 * T / 100) - 1)}`; };
      let tbl = `<table class="band"><tr><th>Correct %</th><th>Band</th>` + (lT ? `<th>Listening<br>(out of ${lT})</th>` : '') + (rT ? `<th>Reading<br>(out of ${rT})</th>` : '') + `</tr>`;
      TH.forEach((p, i) => { tbl += `<tr><td>${p}–${i === 0 ? 100 : TH[i - 1] - 1}%</td><td>${BD[i]}</td>` + (lT ? `<td>${rng(lT, i)}</td>` : '') + (rT ? `<td>${rng(rT, i)}</td>` : '') + `</tr>`; });
      tbl += `<tr><td>below 40%</td><td>3.0</td>` + (lT ? `<td>${below(lT)}</td>` : '') + (rT ? `<td>${below(rT)}</td>` : '') + `</tr></table>`;
      guide += tbl;
    }
    guide += `<h3 class="g">Writing — score each 0–9, then average</h3><ul class="g"><li><strong>Task Achievement / Response</strong> — fully answers the task with relevant, developed ideas.</li><li><strong>Coherence &amp; Cohesion</strong> — logical organisation, paragraphing, linking words.</li><li><strong>Lexical Resource</strong> — range and accuracy of vocabulary.</li><li><strong>Grammatical Range &amp; Accuracy</strong> — variety and correctness of structures.</li></ul>`;
    guide += `<h3 class="g">Speaking — score each 0–9, then average</h3><ul class="g"><li><strong>Fluency &amp; Coherence</strong> — speaks at length, logically, without undue hesitation.</li><li><strong>Lexical Resource</strong> — range and appropriacy of vocabulary.</li><li><strong>Grammatical Range &amp; Accuracy</strong> — correct, varied grammar.</li><li><strong>Pronunciation</strong> — clear and natural; easy to understand.</li></ul>`;
    guide += `<p class="instr">Band anchors: 9 expert · 8 very good · 7 good · 6 competent · 5 modest · 4 limited.</p>`;
  } else if (slug === 'cefr') {
    {
      const T = qs.length;
      const TH = [88, 76, 60, 45, 30], LV = ['C2', 'C1', 'B2', 'B1', 'A2'];
      const rng = (i) => { const lo = Math.ceil(TH[i] * T / 100); const hi = (i === 0) ? T : Math.ceil(TH[i - 1] * T / 100) - 1; return lo >= hi ? `${Math.min(lo, T)}` : `${lo}–${hi}`; };
      let tbl = `<p class="instr">Count the total correct out of ${T} questions, work out the %, then read the level:</p><table class="band"><tr><th>Correct %</th><th>CEFR level</th><th>Correct (out of ${T})</th></tr>`;
      TH.forEach((p, i) => { tbl += `<tr><td>${p}–${i === 0 ? 100 : TH[i - 1] - 1}%</td><td>${LV[i]}</td><td>${rng(i)}</td></tr>`; });
      tbl += `<tr><td>below 30%</td><td>A1</td><td>0–${Math.max(0, Math.ceil(30 * T / 100) - 1)}</td></tr></table>`;
      guide += tbl;
    }
  } else if (slug === 'sat') {
    guide += `<p class="instr">Count correct ÷ total × 100 = overall %. Enter that % in the platform — the SAT-style score is calculated as approximately 400 + (% × 7.6).</p>`;
  } else {
    guide += `<p class="instr">Count correct ÷ total × 100 = overall %. Enter that % in the platform.</p>`;
  }
  guide += `<p class="instr"><strong>Record the result:</strong> in the platform go to Mock Tests → ➕ Paper result → enter the candidate's name and scores → the certificate is generated automatically.</p>`;

  // Loud divider so the tutor pages never get handed to candidates by mistake.
  const tutorDivider = `<div class="endmark">— END OF CANDIDATE PAPER —</div><div class="pb"></div><div class="tutorbanner">⚠ TUTOR / INVIGILATOR SECTION — CONFIDENTIAL<div class="sub">Detach and keep these pages. Do NOT give them to candidates — they contain the listening script and answer key.</div></div>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(tt.name || 'Mock Test')} — Paper</title><style>
    *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#111;max-width:820px;margin:0 auto;padding:24px;line-height:1.5}
    .cover{text-align:center;border-bottom:3px double #333;padding-bottom:16px;margin-bottom:18px}
    .cover h1{font-size:26px;margin:6px 0}.cover .sch{font-weight:bold;letter-spacing:1px}.cover .meta{color:#444;font-size:14px;margin-top:8px}
    .fields{display:flex;gap:28px;justify-content:center;margin-top:14px;font-size:14px}
    .sec{font-size:18px;border-bottom:2px solid #333;padding-bottom:4px;margin:22px 0 6px}.instr{font-style:italic;color:#444;font-size:13px;margin:0 0 12px}
    .sec .time{float:right;font-size:14px;font-weight:normal;color:#00759a}
    .adminnote{font-family:sans-serif;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:12px 16px;margin:10px 0;font-size:13px}
    .adminnote ol{margin:8px 0 0;padding-left:20px}.adminnote li{margin:4px 0}
    .passage{background:#f7f7f5;border:1px solid #ddd;border-left:4px solid #888;padding:12px;margin:10px 0;font-size:14px}
    .q{margin:12px 0;page-break-inside:avoid}.qn{font-weight:bold}.opt{margin:3px 0 3px 22px}.ansrow{margin:8px 0 8px 22px}
    .blank{display:inline-block;border-bottom:1px solid #333;width:240px}.lines{margin:8px 0}.line{border-bottom:1px solid #bbb;height:26px}
    .speak{margin:6px 0 6px 22px;color:#555;font-style:italic}.keygrid{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;font-size:14px}.keyitem{padding:3px 0}
    .pb{page-break-before:always}.printbar{text-align:center;margin-bottom:16px}
    .essay{page-break-before:always;page-break-inside:auto}
    .endmark{text-align:center;font-weight:bold;letter-spacing:1px;color:#444;margin:24px 0;border-top:2px solid #333;padding-top:10px}
    .tutorbanner{border:3px solid #b91c1c;background:#fef2f2;color:#b91c1c;text-align:center;font-weight:bold;font-size:22px;padding:40px 20px;border-radius:10px;margin-top:40px}
    .tutorbanner .sub{font-size:14px;font-weight:normal;color:#7f1d1d;margin-top:12px}
    .tutorhdr{background:#b91c1c;color:#fff;font-family:sans-serif;font-weight:bold;font-size:12px;letter-spacing:1px;text-align:center;padding:6px;border-radius:4px;margin-bottom:10px}
    table.band{border-collapse:collapse;margin:10px 0;font-size:14px}table.band th,table.band td{border:1px solid #999;padding:5px 18px;text-align:left}table.band th{background:#f0f0f0}
    h3.g{font-size:15px;margin:16px 0 4px}ul.g{margin:4px 0;padding-left:20px;font-size:13px}ul.g li{margin:3px 0}
    .printbtn{font-family:sans-serif;background:#00B4D8;color:#fff;border:none;padding:12px 28px;border-radius:8px;font-size:15px;font-weight:bold;cursor:pointer}
    @media print{.printbar{display:none}body{padding:0}}
  </style></head><body>
  <div class="printbar"><button class="printbtn" onclick="window.print()">🖨 Print / Save as PDF</button></div>
  <div class="cover"><div class="sch">${esc(data.school || '')}</div><h1>${esc(tt.name || 'Mock Test')}</h1>
  <div class="meta">Paper-based mock test${tt.duration_minutes ? ` · Suggested time: ${tt.duration_minutes} minutes` : ''}</div>
  <div class="fields"><div>Candidate: ____________________</div><div>Date: ____________</div></div></div>
  ${body}${tutorDivider}${scriptPage}${keyPage}${guide}</body></html>`;
}

const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [tutors, setTutors]     = useState([]);
  useEffect(() => { api.get('/api/admin/tutors').then(r => setTutors((r.data?.tutors) || r.data || [])).catch(() => {}); }, []);
  const assignTutor = async (b, tutorId) => {
    try {
      await api.post(`/api/admin/mock-tests/bookings/${b.id}/assign-tutor`, { tutor_id: tutorId || null });
      toast.success(tutorId ? 'Tutor assigned' : 'Tutor unassigned');
      load();
    } catch { toast.error('Could not assign tutor'); }
  };

  // Confirm Payment modal
  const [payModal, setPayModal]   = useState(null); // booking obj
  const [payMethod, setPayMethod] = useState('Cash');
  const [payAmount, setPayAmount] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // Activate modal
  const [actModal, setActModal]   = useState(null); // booking obj
  const [seatNum,  setSeatNum]    = useState('');
  const [actLoading, setActLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/mock-tests/bookings');
      setBookings(data);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = BOOKING_FILTERS.reduce((acc, f) => {
    if (f.key === 'all') { acc.all = bookings.filter(b => b.test_status !== 'cancelled').length; return acc; }
    if (f.key === 'pending')   acc.pending   = bookings.filter(b => b.payment_status === 'pending').length;
    if (f.key === 'paid')      acc.paid      = bookings.filter(b => b.payment_status === 'paid').length;
    if (f.key === 'activated') acc.activated = bookings.filter(b => b.test_status === 'activated').length;
    if (f.key === 'completed') acc.completed = bookings.filter(b => b.test_status === 'completed').length;
    if (f.key === 'cancelled') acc.cancelled = bookings.filter(b => b.test_status === 'cancelled').length;
    return acc;
  }, {});

  const filtered = bookings.filter(b => {
    if (filter === 'all')       return b.test_status !== 'cancelled';   // cancelled hidden from All
    if (filter === 'pending')   return b.payment_status === 'pending';
    if (filter === 'paid')      return b.payment_status === 'paid';
    if (filter === 'activated') return b.test_status === 'activated';
    if (filter === 'completed') return b.test_status === 'completed';
    if (filter === 'cancelled') return b.test_status === 'cancelled';
    return true;
  });

  const confirmPayment = async () => {
    if (!payAmount) { toast.error('Enter amount'); return; }
    setPayLoading(true);
    try {
      const { data } = await api.post(`/api/admin/mock-tests/bookings/${payModal.id}/confirm-payment`, {
        payment_method: payMethod,
        amount: parseFloat(payAmount),
      });
      toast.success('Payment confirmed');
      if (data && data.login) {
        if (data.login.reused) {
          window.alert('This candidate already has an account (' + data.login.email + ').\nThey sign in at ' + data.login.login_url + ' and open "My Mock Tests".');
        } else {
          window.alert(
            'Mock candidate login created — give these to the candidate:\n\n' +
            'Login page:  ' + data.login.login_url + '\n' +
            'Email:       ' + data.login.email + '\n' +
            'Password:    ' + data.login.temp_password + '\n\n' +
            'They log in and open "My Mock Tests" to start. (This is a mock-only login, separate from course enrolment.)'
          );
        }
      }
      setPayModal(null);
      setPayAmount('');
      load();
    } catch {
      toast.error('Failed to confirm payment');
    } finally {
      setPayLoading(false);
    }
  };

  const resendMock = async (bid) => {
    try {
      const r = await api.post(`/api/admin/mock-tests/bookings/${bid}/resend`);
      toast.success(r.data?.message || 'Confirmation re-sent');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to resend'); }
  };

  const getMockLogin = async (bid) => {
    try {
      const { data: d } = await api.post(`/api/admin/mock-tests/bookings/${bid}/login`);
      if (d.is_candidate) {
        window.alert('Candidate login — give these to the student:\n\n' +
          'Login page:  ' + d.login_url + '\n' +
          'Email:       ' + d.email + '\n' +
          'Password:    ' + d.temp_password + '\n\n' +
          '(A fresh password was just generated for them.)');
      } else {
        window.alert(d.note + '\n\nEmail: ' + d.email + '\nLogin page: ' + d.login_url);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to get login'); }
  };

  const activateTest = async () => {
    setActLoading(true);
    try {
      await api.post(`/api/admin/mock-tests/bookings/${actModal.id}/activate`, {
        seat_number: seatNum,
      });
      toast.success('Test activated');
      setActModal(null);
      setSeatNum('');
      load();
    } catch {
      toast.error('Failed to activate test');
    } finally {
      setActLoading(false);
    }
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {BOOKING_FILTERS.map(f => {
          const active = filter === f.key;
          const count  = counts[f.key] ?? 0;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '7px 14px',
              borderRadius: 8,
              border: `1px solid ${active ? C.teal : C.border}`,
              background: active ? C.teal : '#fff',
              color: active ? '#fff' : C.grey,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
              {f.label}
              {count > 0 && (
                <span style={{
                  marginLeft: 6, background: active ? 'rgba(255,255,255,0.25)' : C.bg,
                  color: active ? '#fff' : C.navy,
                  borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.grey }}>Loading bookings…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.grey }}>No bookings found.</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${C.border}`, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <TH>Student</TH>
                <TH>Phone</TH>
                <TH>Email</TH>
                <TH>Test Type</TH>
                <TH>Slot</TH>
                <TH>Payment</TH>
                <TH>Test Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => (
                <tr key={b.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <TD><span style={{ fontWeight: 600 }}>{b.student_name}</span></TD>
                  <TD>{b.student_phone || '—'}</TD>
                  <TD style={{ fontSize: 13, color: C.grey }}>{b.student_email}</TD>
                  <TD>{b.test_type?.name || '—'}</TD>
                  <TD>
                    {b.slot ? (
                      <span>{fmt(b.slot.slot_date)}<br />
                        <span style={{ fontSize: 12, color: C.grey }}>{fmtTime(b.slot.slot_time)}</span>
                      </span>
                    ) : '—'}
                  </TD>
                  <TD>{paymentBadge(b.payment_status)}</TD>
                  <TD>{testStatusBadge(b.test_status)}</TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {b.payment_status === 'pending' && (
                        <Btn onClick={() => { setPayModal(b); setPayAmount(String(b.amount_paid_usd || b.price_usd || '')); }} style={{ fontSize: 12, padding: '5px 10px' }}>
                          Confirm Payment
                        </Btn>
                      )}
                      {b.payment_status === 'paid' && b.test_status === 'booked' && (
                        <Btn onClick={() => { setActModal(b); setSeatNum(''); }} color={C.success} style={{ fontSize: 12, padding: '5px 10px' }}>
                          Activate Test
                        </Btn>
                      )}
                      <GhostBtn onClick={() => resendMock(b.id)} style={{ fontSize: 12, padding: '5px 10px' }}>✉ Resend</GhostBtn>
                      <GhostBtn onClick={() => getMockLogin(b.id)} style={{ fontSize: 12, padding: '5px 10px' }}>🔑 Login</GhostBtn>
                    </div>
                    {b.payment_status === 'paid' && (
                      <div style={{ marginTop: 6 }}>
                        <select value={(tutors.find(t => t.user_id === b.assigned_tutor_id)?.id) || ''}
                          onChange={e => assignTutor(b, e.target.value)}
                          style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: `1px solid ${b.assigned_tutor_id ? C.success : C.border}`, background: b.assigned_tutor_id ? '#ECFDF5' : '#fff', maxWidth: 170 }}>
                          <option value="">🎓 Assign tutor…</option>
                          {tutors.map(t => (
                            <option key={t.id} value={t.id}>{t.full_name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || ('Tutor ' + t.id)}</option>
                          ))}
                        </select>
                        {b.assigned_tutor_id && !tutors.find(t => t.user_id === b.assigned_tutor_id) && b.assigned_tutor_name && (
                          <span style={{ fontSize: 11, color: C.success, marginLeft: 6 }}>🎓 {b.assigned_tutor_name}</span>
                        )}
                      </div>
                    )}
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm Payment Modal */}
      {payModal && (
        <Modal title="Confirm Payment" onClose={() => setPayModal(null)}>
          <p style={{ margin: '0 0 8px', color: C.grey, fontSize: 14 }}>
            Confirming payment for <strong style={{ color: C.navy }}>{payModal.student_name}</strong>
            {payModal.test_type?.name ? <> · <strong style={{ color: C.navy }}>{payModal.test_type.name}</strong></> : null}
          </p>
          {payModal.price_usd != null && (
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.grey }}>
              List price for this test: <strong style={{ color: C.teal }}>${Number(payModal.price_usd).toFixed(2)}</strong> (pre-filled below — edit if they paid a different amount)
            </p>
          )}
          <Field label="Payment Method">
            <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={selectStyle}>
              {['Cash', 'Card', 'Payme', 'Click'].map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Amount (USD)">
            <input
              type="number" step="0.01" min="0"
              value={payAmount}
              onChange={e => setPayAmount(e.target.value)}
              placeholder="Enter amount"
              style={inputStyle}
            />
          </Field>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <GhostBtn onClick={() => setPayModal(null)}>Cancel</GhostBtn>
            <Btn onClick={confirmPayment} disabled={payLoading}>
              {payLoading ? 'Confirming…' : 'Confirm Payment'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Activate Test Modal */}
      {actModal && (
        <Modal title="Activate Test" onClose={() => setActModal(null)}>
          <p style={{ margin: '0 0 20px', color: C.grey, fontSize: 14 }}>
            Activating test for <strong style={{ color: C.navy }}>{actModal.student_name}</strong>
          </p>
          <Field label="Seat Number (optional)">
            <input
              type="text"
              value={seatNum}
              onChange={e => setSeatNum(e.target.value)}
              placeholder="e.g. A3"
              style={inputStyle}
            />
          </Field>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <GhostBtn onClick={() => setActModal(null)}>Cancel</GhostBtn>
            <Btn onClick={activateTest} disabled={actLoading} color={C.success}>
              {actLoading ? 'Activating…' : 'Activate Test'}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: SLOTS
// ═══════════════════════════════════════════════════════════════════════════════
const SlotsTab = ({ testTypes }) => {
  const [slots,   setSlots]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tutors,  setTutors]  = useState([]);

  useEffect(() => {
    api.get('/api/admin/tutors').then(r => setTutors((r.data?.tutors) || [])).catch(() => {});
  }, []);

  // Add modal
  const [showAdd,  setShowAdd]  = useState(false);
  const [addForm,  setAddForm]  = useState({
    test_type_id: '', slot_date: '', slot_time: '09:00',
    duration_minutes: '', capacity: 10, room_location: '', notes: '', invigilator_user_id: '',
  });
  const [addLoading, setAddLoading] = useState(false);

  // Edit modal
  const [editSlot,   setEditSlot]   = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/mock-tests/slots');
      setSlots(data);
    } catch {
      toast.error('Failed to load slots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Set default duration when test type changes
  const handleAddTypeChange = (e) => {
    const id  = e.target.value;
    const tt  = testTypes.find(t => String(t.id) === String(id));
    setAddForm(f => ({ ...f, test_type_id: id, duration_minutes: tt ? tt.duration_minutes : '' }));
  };

  const addSlot = async () => {
    if (!addForm.test_type_id || !addForm.slot_date || !addForm.slot_time) {
      toast.error('Test type, date and time are required');
      return;
    }
    setAddLoading(true);
    try {
      await api.post('/api/admin/mock-tests/slots', {
        ...addForm,
        capacity: parseInt(addForm.capacity, 10) || 10,
        duration_minutes: parseInt(addForm.duration_minutes, 10) || 60,
      });
      toast.success('Slot added');
      setShowAdd(false);
      setAddForm({ test_type_id: '', slot_date: '', slot_time: '09:00', duration_minutes: '', capacity: 10, room_location: '', notes: '', invigilator_user_id: '' });
      load();
    } catch {
      toast.error('Failed to add slot');
    } finally {
      setAddLoading(false);
    }
  };

  const openEdit = (slot) => {
    setEditSlot(slot);
    setEditForm({ capacity: slot.capacity, room_location: slot.room_location || '', notes: slot.notes || '' });
  };

  const saveEdit = async () => {
    setEditLoading(true);
    try {
      await api.patch(`/api/admin/mock-tests/slots/${editSlot.id}`, {
        capacity:      parseInt(editForm.capacity, 10),
        room_location: editForm.room_location,
        notes:         editForm.notes,
      });
      toast.success('Slot updated');
      setEditSlot(null);
      load();
    } catch {
      toast.error('Failed to update slot');
    } finally {
      setEditLoading(false);
    }
  };

  const deleteSlot = async (slot) => {
    if (!window.confirm(`Delete slot on ${fmt(slot.slot_date)} at ${fmtTime(slot.slot_time)}?`)) return;
    try {
      await api.delete(`/api/admin/mock-tests/slots/${slot.id}`);
      toast.success('Slot deleted');
      load();
    } catch {
      toast.error('Failed to delete slot');
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = slots.filter(s => s.slot_date >= today);
  const past     = slots.filter(s => s.slot_date <  today);

  const SlotCard = ({ slot }) => {
    const pct = slot.capacity > 0 ? Math.round((slot.booked_count / slot.capacity) * 100) : 0;
    const barColor = pct >= 100 ? C.error : pct >= 75 ? C.amber : C.teal;
    return (
      <div style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.navy }}>{slot.test_type?.name || '—'}</div>
            <div style={{ fontSize: 13, color: C.grey, marginTop: 2 }}>
              {fmt(slot.slot_date)} &nbsp;·&nbsp; {fmtTime(slot.slot_time)}
              &nbsp;·&nbsp; {slot.duration_minutes} min
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <GhostBtn onClick={() => openEdit(slot)} style={{ padding: '5px 12px', fontSize: 12 }}>Edit</GhostBtn>
            <Btn onClick={() => deleteSlot(slot)} color={C.error} style={{ padding: '5px 12px', fontSize: 12 }}>Delete</Btn>
          </div>
        </div>

        {/* Capacity bar */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.grey, marginBottom: 4 }}>
            <span>Capacity</span>
            <span style={{ fontWeight: 600 }}>{slot.booked_count}/{slot.capacity} booked</span>
          </div>
          <div style={{ background: C.bg, borderRadius: 6, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: barColor, borderRadius: 6, transition: 'width .3s' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, fontSize: 13, color: C.grey, flexWrap: 'wrap' }}>
          {slot.room_location && <span>Room: <strong style={{ color: C.navy }}>{slot.room_location}</strong></span>}
          {slot.notes && <span>Notes: {slot.notes}</span>}
        </div>
      </div>
    );
  };

  const Section = ({ title, items }) => (
    <div style={{ marginBottom: 28 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.grey, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title} ({items.length})
      </h4>
      {items.length === 0
        ? <p style={{ color: C.grey, fontSize: 14 }}>No slots.</p>
        : <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))' }}>
            {items.map(s => <SlotCard key={s.id} slot={s} />)}
          </div>
      }
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <Btn onClick={() => setShowAdd(true)}>+ Add Slot</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.grey }}>Loading slots…</div>
      ) : (
        <>
          <Section title="Upcoming" items={upcoming} />
          <Section title="Past"     items={past} />
        </>
      )}

      {/* Add Slot Modal */}
      {showAdd && (
        <Modal title="Add Time Slot" onClose={() => setShowAdd(false)}>
          <Field label="Test Type *">
            <select value={addForm.test_type_id} onChange={handleAddTypeChange} style={selectStyle}>
              <option value="">Select test type…</option>
              {testTypes.map(tt => (
                <option key={tt.id} value={tt.id}>{tt.name}</option>
              ))}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Date *">
              <input type="date" value={addForm.slot_date}
                onChange={e => setAddForm(f => ({ ...f, slot_date: e.target.value }))}
                style={inputStyle} />
            </Field>
            <Field label="Time *">
              <input type="time" value={addForm.slot_time}
                onChange={e => setAddForm(f => ({ ...f, slot_time: e.target.value }))}
                style={inputStyle} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Duration (min)">
              <input type="number" min="1" value={addForm.duration_minutes}
                onChange={e => setAddForm(f => ({ ...f, duration_minutes: e.target.value }))}
                placeholder="60"
                style={inputStyle} />
            </Field>
            <Field label="Capacity">
              <input type="number" min="1" value={addForm.capacity}
                onChange={e => setAddForm(f => ({ ...f, capacity: e.target.value }))}
                style={inputStyle} />
            </Field>
          </div>
          <Field label="Room / Location">
            <input type="text" value={addForm.room_location}
              onChange={e => setAddForm(f => ({ ...f, room_location: e.target.value }))}
              placeholder="e.g. Room 101"
              style={inputStyle} />
          </Field>
          <Field label="Invigilator (tutor)">
            <select value={addForm.invigilator_user_id}
              onChange={e => setAddForm(f => ({ ...f, invigilator_user_id: e.target.value }))}
              style={selectStyle}>
              <option value="">— Admin invigilates —</option>
              {tutors.map(t => (
                <option key={t.id} value={t.user_id}>{t.full_name || `${t.first_name || ''} ${t.last_name || ''}`}</option>
              ))}
            </select>
          </Field>
          <Field label="Notes">
            <textarea value={addForm.notes}
              onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Any notes for this slot…"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <GhostBtn onClick={() => setShowAdd(false)}>Cancel</GhostBtn>
            <Btn onClick={addSlot} disabled={addLoading}>
              {addLoading ? 'Adding…' : 'Add Slot'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Edit Slot Modal */}
      {editSlot && (
        <Modal title="Edit Slot" onClose={() => setEditSlot(null)}>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: C.grey }}>
            <strong style={{ color: C.navy }}>{editSlot.test_type?.name}</strong> &nbsp;—&nbsp;
            {fmt(editSlot.slot_date)} at {fmtTime(editSlot.slot_time)}
          </p>
          <Field label="Capacity">
            <input type="number" min="1" value={editForm.capacity}
              onChange={e => setEditForm(f => ({ ...f, capacity: e.target.value }))}
              style={inputStyle} />
          </Field>
          <Field label="Room / Location">
            <input type="text" value={editForm.room_location}
              onChange={e => setEditForm(f => ({ ...f, room_location: e.target.value }))}
              placeholder="e.g. Room 101"
              style={inputStyle} />
          </Field>
          <Field label="Notes">
            <textarea value={editForm.notes}
              onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <GhostBtn onClick={() => setEditSlot(null)}>Cancel</GhostBtn>
            <Btn onClick={saveEdit} disabled={editLoading}>
              {editLoading ? 'Saving…' : 'Save Changes'}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: PRICING
// ═══════════════════════════════════════════════════════════════════════════════
const PricingTab = ({ testTypes }) => {
  const [prices,  setPrices]  = useState({});
  const [saving,  setSaving]  = useState(false);

  // Init prices from testTypes
  useEffect(() => {
    const init = {};
    testTypes.forEach(tt => {
      init[tt.id] = tt.tenant_price != null ? String(tt.tenant_price) : '';
    });
    setPrices(init);
  }, [testTypes]);

  const savePrices = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(prices).map(([test_type_id, price_usd]) => ({
        test_type_id: parseInt(test_type_id, 10),
        price_usd: parseFloat(price_usd) || 0,
      }));
      await api.post('/api/admin/mock-tests/prices', { prices: payload });
      toast.success('Prices saved');
    } catch {
      toast.error('Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{
        background: '#FEF9C3',
        border: `1px solid #FDE68A`,
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: 20,
        fontSize: 13,
        color: '#78350F',
      }}>
        These are the prices students pay at your centre. Default prices set by SpeakIQ are shown as placeholders.
      </div>

      <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${C.border}`, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <TH>Test Name</TH>
              <TH>Duration</TH>
              <TH>Sections</TH>
              <TH>Your Price (USD)</TH>
            </tr>
          </thead>
          <tbody>
            {testTypes.map((tt, i) => (
              <tr key={tt.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                <TD><span style={{ fontWeight: 600 }}>{tt.name}</span></TD>
                <TD>{tt.duration_minutes} min</TD>
                <TD>
                  {Array.isArray(tt.sections)
                    ? tt.sections.map(s => (
                        <span key={s} style={{
                          display: 'inline-block', marginRight: 4, marginBottom: 2,
                          padding: '2px 8px', borderRadius: 10,
                          background: C.bg, fontSize: 11, color: C.navy, fontWeight: 600,
                        }}>{s}</span>
                      ))
                    : tt.sections || '—'}
                </TD>
                <TD>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 180 }}>
                    <span style={{ color: C.grey }}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={prices[tt.id] || ''}
                      onChange={e => setPrices(p => ({ ...p, [tt.id]: e.target.value }))}
                      placeholder={tt.default_price_usd ? String(tt.default_price_usd) : '0.00'}
                      style={{ ...inputStyle, maxWidth: 120 }}
                    />
                  </div>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <Btn onClick={savePrices} disabled={saving} style={{ padding: '10px 28px', fontSize: 15 }}>
          {saving ? 'Saving…' : 'Save Prices'}
        </Btn>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: RESULTS
// ═══════════════════════════════════════════════════════════════════════════════
const RESULT_FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'submitted', label: 'Awaiting Marking' },
  { key: 'marked',    label: 'Marked' },
  { key: 'history',   label: '🗄 History' },
];

const ResultsTab = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');
  const [source,  setSource]  = useState('all');   // all | walkin | registered
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [query,   setQuery]   = useState('');

  // Mark modal
  const [markModal,  setMarkModal]  = useState(null);
  const [writingScore, setWritingScore] = useState('');
  const [speakingScore, setSpeakingScore] = useState('');
  const [sectionBands, setSectionBands] = useState({});
  const [markResponses, setMarkResponses] = useState(null);
  const [examNotes, setExamNotes] = useState('');
  const [markLoading,  setMarkLoading]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'history' ? '/api/admin/mock-tests/results?archived=1' : '/api/admin/mock-tests/results';
      const { data } = await api.get(url);
      setResults(data);
    } catch {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const archiveSession = async (r, archived) => {
    try { await api.post(`/api/admin/mock-tests/sessions/${r.id}/archive`, { archived }); toast.success(archived ? 'Moved to History' : 'Restored to Results'); load(); }
    catch { toast.error('Action failed'); }
  };
  const deleteSession = async (r) => {
    if (!window.confirm(`Permanently delete the result for ${r.student_name || 'this candidate'}? This also deletes its certificate — its verification link will stop working. This cannot be undone.`)) return;
    try { await api.delete(`/api/admin/mock-tests/sessions/${r.id}`); toast.success('Deleted permanently'); load(); }
    catch (e) { toast.error(e?.response?.data?.error || 'Could not delete'); }
  };

  const filtered = results.filter(r => {
    if (filter !== 'all' && filter !== 'history' && r.status !== filter) return false;
    if (source === 'walkin' && !r.is_walkin) return false;
    if (source === 'registered' && r.is_walkin) return false;
    const d = (r.submitted_at || '').slice(0, 10);
    if (dateFrom && d && d < dateFrom) return false;
    if (dateTo && d && d > dateTo) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const hay = `${r.student_name || ''} ${r.student_email || ''} ${r.certificate?.verification_code || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const certsInView = filtered.filter(r => r.certificate);

  const counts = {
    all:       results.length,
    submitted: results.filter(r => r.status === 'submitted').length,
    marked:    results.filter(r => r.status === 'marked').length,
  };

  const isIelts = (session) => (session?.score_mode === 'band') || session?.test_type?.slug?.toLowerCase().includes('ielts');

  const openMark = (session) => {
    setMarkModal(session);
    setWritingScore(session.writing_score != null ? String(session.writing_score) : '');
    setSpeakingScore(session.speaking_score != null ? String(session.speaking_score) : '');
    setSectionBands({});
    setMarkResponses(null);
    setExamNotes(session.examiner_notes || '');
    api.get(`/api/admin/mock-tests/sessions/${session.id}/responses`)
      .then(r => setMarkResponses(r.data)).catch(() => setMarkResponses({ writing: [], speaking: [] }));
  };

  // IELTS overall = average of auto Listening/Reading bands + entered Writing/Speaking.
  const ieltsOverall = () => {
    const auto = markModal?.section_scores || {};
    const bands = [];
    ['listening', 'reading'].forEach(k => {
      const p = auto[k]?.percent;
      if (p != null) bands.push(p >= 90 ? 9 : p >= 80 ? 8 : p >= 70 ? 7 : p >= 60 ? 6 : p >= 50 ? 5 : p >= 40 ? 4 : 3);
    });
    if (writingScore !== '' && !isNaN(parseFloat(writingScore))) bands.push(parseFloat(writingScore));
    if (speakingScore !== '' && !isNaN(parseFloat(speakingScore))) bands.push(parseFloat(speakingScore));
    if (!bands.length) return null;
    return Math.round((bands.reduce((a, b) => a + b, 0) / bands.length) * 2) / 2;
  };

  const openCertificate = async (downloadUrl) => {
    try {
      const r = await api.get(downloadUrl, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open certificate'); }
  };
  // Download the actual PDF file (so the admin can attach it in WhatsApp/Telegram).
  const downloadCertificate = async (cert) => {
    try {
      const r = await api.get(cert.download_url, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `certificate-${cert.verification_code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success('PDF downloaded — attach it in WhatsApp or Telegram');
    } catch { toast.error('Could not download certificate'); }
  };
  // Share the actual PDF file via the device's native share sheet → Telegram / WhatsApp
  // attach the real file (not a link). Falls back to download where unsupported.
  const shareCertificate = async (cert, studentName) => {
    try {
      const res = await api.get(cert.download_url, { responseType: 'blob' });
      const file = new File([res.data], `certificate-${cert.verification_code}.pdf`, { type: 'application/pdf' });
      // File-sharing via the native share sheet only actually attaches the PDF on
      // phones/tablets. On desktop the share target drops the file (text only), so
      // we download instead and let the admin drag it into Telegram/WhatsApp.
      const isTouch = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints || 0) > 1;
      if (isTouch && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${studentName || 'Student'} — Mock Test Certificate` });
        return;
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast('PDF downloaded — drag it into your Telegram/WhatsApp chat to send.', { icon: '⬇', duration: 6000 });
    } catch (e) {
      if (e && e.name === 'AbortError') return;   // user closed the share sheet
      toast.error('Could not share certificate');
    }
  };
  // Email the certificate PDF straight to the student (real attachment, no link).
  const emailCertificate = async (row) => {
    const email = window.prompt(`Email the certificate PDF to ${row.student_name || 'the student'} — enter their email address:`);
    if (!email || !email.trim()) return;
    try {
      const res = await api.post(`/api/admin/mock-tests/sessions/${row.id}/email-certificate`, { email: email.trim() });
      toast.success(res.data?.message || 'Certificate emailed');
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not send email'); }
  };

  // Full printable test transcript — for disputes / appeals / manual review.
  const printReview = () => {
    const r = markResponses;
    if (!r) { toast.error('Responses still loading…'); return; }
    const m = markModal;
    const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const overall = m.band_score ? `Band ${esc(m.band_score)}` : (m.total_score != null ? `${Math.round(m.total_score)}%` : '—');
    let h = `<html><head><title>Mock Test Review — ${esc(m.student_name)}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;color:#1f2937;padding:28px;max-width:800px;margin:auto;}
      h1{font-size:22px;margin:0 0 4px;} h2{font-size:15px;border-bottom:2px solid #00B4D8;padding-bottom:4px;margin:22px 0 8px;}
      .meta{color:#555;font-size:13px;margin:2px 0;} .q{margin:8px 0;padding:8px 0;border-bottom:1px solid #eee;font-size:13px;}
      .ok{color:#10B981;font-weight:bold;} .no{color:#EF4444;font-weight:bold;} img{max-width:480px;border:1px solid #ddd;border-radius:6px;margin:6px 0;}
      .resp{background:#f8fafc;border:1px solid #e5e7eb;border-radius:6px;padding:10px;white-space:pre-wrap;}
      @media print{button{display:none;}}</style></head><body>`;
    h += `<h1>Mock Test Review</h1>`;
    h += `<div class="meta">Student: <b>${esc(m.student_name)}</b> &nbsp;·&nbsp; Test: <b>${esc(m.test_type?.name || '')}</b></div>`;
    h += `<div class="meta">Status: ${esc(m.status)} &nbsp;·&nbsp; Overall: <b>${overall}</b>${m.flagged ? ' &nbsp;·&nbsp; <span style="color:#EF4444">⚠ Flagged for tab-switching</span>' : ''}</div>`;
    if (m.started_at && m.submitted_at) {
      const secs = (new Date(m.submitted_at) - new Date(m.started_at)) / 1000;
      h += `<div class="meta">Time taken: ${Math.floor(secs/60)}m ${Math.floor(secs%60)}s of ${Math.round((m.time_limit_seconds||0)/60)}m allowed</div>`;
    }
    if (m.section_scores && Object.keys(m.section_scores).length) {
      h += `<h2>Section scores</h2>`;
      Object.entries(m.section_scores).forEach(([s, v]) => { h += `<div class="meta">${esc(prettyName(s))}: <b>${esc(sectionDisplay(v))}</b></div>`; });
    }
    if (r.objective?.length) {
      h += `<h2>Auto-marked answers (${r.objective.filter(o => o.is_correct).length}/${r.objective.length} correct)</h2>`;
      r.objective.forEach((o, i) => {
        h += `<div class="q"><b>${i + 1}. [${esc(prettyName(o.section))}]</b> ${esc(o.question_text)}<br/>Student: <span class="${o.is_correct ? 'ok' : 'no'}">${esc(o.answer || '—')}</span> &nbsp;·&nbsp; Correct: <b>${esc(o.correct_answer)}</b> &nbsp;${o.is_correct === true ? '✓' : o.is_correct === false ? '✗' : ''}</div>`;
      });
    }
    (r.writing || []).forEach((w, i) => {
      h += `<h2>Writing response ${i + 1}</h2><div class="q">${esc(w.question_text)}</div>`;
      if (w.image_url) h += `<img src="${window.location.origin}${esc(w.image_url)}"/>`;
      h += `<div class="resp">${esc(w.answer) || '<i>No response written</i>'}</div><div class="meta">${w.word_count} words</div>`;
    });
    if (r.speaking?.length) {
      h += `<h2>Speaking prompts (conducted live)</h2>`;
      r.speaking.forEach(s => { h += `<div class="q">${esc(s.question_text)}</div>`; });
    }
    if (m.examiner_notes) h += `<h2>Examiner notes</h2><div class="resp">${esc(m.examiner_notes)}</div>`;
    h += `<p style="margin-top:30px;color:#999;font-size:11px;">Generated by SpeakIQ · ${esc(m.student_email || '')}</p></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups to print the review'); return; }
    w.document.write(h); w.document.close(); w.focus();
    setTimeout(() => w.print(), 500);
  };

  const submitMark = async () => {
    const ielts = isIelts(markModal);
    const payload = ielts ? {
      writing_score:  writingScore  !== '' ? parseFloat(writingScore)  : undefined,
      speaking_score: speakingScore !== '' ? parseFloat(speakingScore) : undefined,
    } : {};
    payload.examiner_notes = examNotes;
    setMarkLoading(true);
    try {
      await api.post(`/api/admin/mock-tests/sessions/${markModal.id}/mark`, payload);
      toast.success(ielts ? `Marked · overall band ${ieltsOverall() ?? ''}` : 'Result confirmed & certificate issued');
      setMarkModal(null);
      load();
    } catch {
      toast.error('Failed to mark session');
    } finally {
      setMarkLoading(false);
    }
  };

  const prettyName = (s) => String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const sectionDisplay = (val) => {
    if (val && typeof val === 'object') {
      if (val.band != null) return Number(val.band).toFixed(1);
      if (val.percent != null) return `${Math.round(val.percent)}%`;
      if (val.correct != null && val.total != null) return `${val.correct}/${val.total}`;
      return '';
    }
    if (typeof val === 'number') return val <= 9 ? val.toFixed(1) : `${Math.round(val)}%`;
    return String(val ?? '');
  };
  const SectionPills = ({ scores }) => {
    if (!scores || typeof scores !== 'object' || !Object.keys(scores).length) return <span style={{ color: C.grey }}>—</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {Object.entries(scores).map(([sec, val]) => (
          <span key={sec} style={{
            padding: '2px 8px', borderRadius: 10,
            background: C.bg, fontSize: 11, fontWeight: 600, color: C.navy,
          }}>
            {prettyName(sec)}: {sectionDisplay(val)}
          </span>
        ))}
      </div>
    );
  };

  const downloadAllZip = async () => {
    const ids = certsInView.map(r => r.id);
    if (!ids.length) { toast.error('No certificates match the current filter'); return; }
    try {
      toast(`Preparing ${ids.length} certificate(s)…`);
      const res = await api.post('/api/admin/mock-tests/certificates-zip', { session_ids: ids }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'certificates.zip';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success(`${ids.length} certificate(s) downloaded`);
    } catch (e) {
      toast.error('Could not prepare the ZIP');
    }
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
        {RESULT_FILTERS.map(f => {
          const active = filter === f.key;
          const count  = counts[f.key] ?? 0;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '7px 14px',
              borderRadius: 8,
              border: `1px solid ${active ? C.teal : C.border}`,
              background: active ? C.teal : '#fff',
              color: active ? '#fff' : C.grey,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
              {f.label}
              {count > 0 && (
                <span style={{
                  marginLeft: 6, background: active ? 'rgba(255,255,255,0.25)' : C.bg,
                  color: active ? '#fff' : C.navy,
                  borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700,
                }}>{count}</span>
              )}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="🔍 Search name / email / code"
            style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, width: 220 }} />
          <select value={source} onChange={e => setSource(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, color: C.navy, background: '#fff', cursor: 'pointer' }}>
            <option value="all">All candidates</option>
            <option value="walkin">Walk-ins only</option>
            <option value="registered">Registered only</option>
          </select>
          <label style={{ fontSize: 12, color: C.grey }}>From <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} /></label>
          <label style={{ fontSize: 12, color: C.grey }}>To <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }} /></label>
          <button onClick={downloadAllZip} disabled={certsInView.length === 0}
            title="Download the certificates matching the current filter as one ZIP, named by student"
            style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.teal}`, background: certsInView.length ? C.teal : '#fff', color: certsInView.length ? '#fff' : C.grey, fontWeight: 700, fontSize: 13, cursor: certsInView.length ? 'pointer' : 'not-allowed' }}>
            ⬇ Download {certsInView.length} certificate{certsInView.length === 1 ? '' : 's'} (ZIP)
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.grey }}>Loading results…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: C.grey }}>No results found.</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${C.border}`, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <TH>Student</TH>
                <TH>Email</TH>
                <TH>Test Type</TH>
                <TH>Score</TH>
                <TH>Band / Level</TH>
                <TH>Sections</TH>
                <TH>Status</TH>
                <TH>Flagged</TH>
                <TH>Date</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <TD><span style={{ fontWeight: 600 }}>{r.student_name}</span></TD>
                  <TD style={{ fontSize: 13, color: C.grey }}>{r.student_email}</TD>
                  <TD>{r.test_type?.name || '—'}</TD>
                  <TD>
                    {r.test_type?.slug === 'ielts'
                      ? <span style={{ color: C.grey }}>—</span>
                      : r.total_score != null
                      ? <span style={{ fontWeight: 700, color: C.navy }}>{Math.round(r.total_score)}%</span>
                      : <span style={{ color: C.grey }}>—</span>
                    }
                  </TD>
                  <TD>
                    {r.band_score != null && (
                      <span style={{ fontWeight: 700, color: C.teal, marginRight: 4 }}>{r.band_score}</span>
                    )}
                    {r.cefr_result && r.test_type?.slug === 'cefr' && (
                      <Badge label={r.cefr_result} color='#1D4ED8' bg='#DBEAFE' />
                    )}
                    {r.band_score == null && !(r.cefr_result && r.test_type?.slug === 'cefr') && '—'}
                  </TD>
                  <TD><SectionPills scores={r.section_scores} /></TD>
                  <TD>{resultStatusBadge(r.status)}</TD>
                  <TD>
                    {r.flagged
                      ? <span style={{ fontSize: 18 }} title="Tab switched 3+ times">⚠️</span>
                      : <span style={{ color: C.grey, fontSize: 13 }}>—</span>
                    }
                  </TD>
                  <TD style={{ fontSize: 12, color: C.grey, whiteSpace: 'nowrap' }}>
                    {fmtDateTime(r.submitted_at)}
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {r.status === 'submitted' && (
                        <Btn onClick={() => openMark(r)} style={{ fontSize: 12, padding: '5px 12px' }}>
                          Mark
                        </Btn>
                      )}
                      {r.status === 'marked' && (
                        <GhostBtn onClick={() => openMark(r)} style={{ fontSize: 12, padding: '5px 10px' }}>
                          🔍 Review
                        </GhostBtn>
                      )}
                      {r.certificate && (
                        <>
                          <Btn onClick={() => shareCertificate(r.certificate, r.student_name)} style={{ fontSize: 12, padding: '5px 10px' }}>
                            📤 Share PDF
                          </Btn>
                          <GhostBtn onClick={() => openCertificate(r.certificate.download_url)} style={{ fontSize: 12, padding: '5px 10px' }}>
                            👁 View
                          </GhostBtn>
                          <GhostBtn onClick={() => downloadCertificate(r.certificate)} style={{ fontSize: 12, padding: '5px 10px' }}>
                            ⬇ Download
                          </GhostBtn>
                        </>
                      )}
                      {filter === 'history' ? (
                        <>
                          <GhostBtn onClick={() => archiveSession(r, false)} style={{ fontSize: 12, padding: '5px 10px' }}>↩ Restore</GhostBtn>
                          <GhostBtn onClick={() => deleteSession(r)} style={{ fontSize: 12, padding: '5px 10px', color: '#DC2626' }}>🗑 Delete</GhostBtn>
                        </>
                      ) : (
                        <GhostBtn onClick={() => archiveSession(r, true)} style={{ fontSize: 12, padding: '5px 10px', color: C.grey }}>🗄 Clear</GhostBtn>
                      )}
                    </div>
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mark Modal */}
      {markModal && (
        <Modal title="Mark Session" onClose={() => setMarkModal(null)}>
          <p style={{ margin: '0 0 4px', fontSize: 14, color: C.grey }}>
            Student: <strong style={{ color: C.navy }}>{markModal.student_name}</strong>
          </p>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: C.grey }}>
            Test: <strong style={{ color: C.navy }}>{markModal.test_type?.name}</strong>
          </p>

          {/* Auto-scored section results (always shown) */}
          {markModal.section_scores && Object.keys(markModal.section_scores).length > 0 && (
            <div style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.grey, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {isIelts(markModal) ? 'Auto-scored sections' : 'Section results (auto-scored)'}
              </div>
              {Object.entries(markModal.section_scores).map(([sec, val]) => (
                <div key={sec} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: C.navy }}>
                  <span>{prettyName(sec)}</span>
                  <strong>{sectionDisplay(val)}</strong>
                </div>
              ))}
              {markModal.total_score != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingTop: 8, marginTop: 6, borderTop: `1px solid ${C.border}`, color: C.navy }}>
                  <span style={{ fontWeight: 700 }}>Overall</span>
                  <strong>{isIelts(markModal) && markModal.band_score ? `Band ${markModal.band_score}` : `${Math.round(markModal.total_score)}%`}</strong>
                </div>
              )}
            </div>
          )}

          {/* Student's tutor-marked work — read before banding */}
          {markResponses && ((markResponses.writing || []).length > 0 || (markResponses.speaking || []).length > 0 || (markResponses.objective || []).length > 0) && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16, maxHeight: 360, overflowY: 'auto' }}>
              {(markResponses.objective || []).length > 0 && (
                <div style={{ padding: 14, borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.success, textTransform: 'uppercase', marginBottom: 8 }}>
                    Auto-marked answers ({(markResponses.objective || []).filter(o => o.is_correct).length}/{(markResponses.objective || []).length} correct) — spot-check
                  </div>
                  {(markResponses.objective || []).map((o, i) => (
                    <div key={'o'+i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '4px 0', borderTop: i ? `1px solid #F1F5F9` : 'none' }}>
                      <span style={{ color: C.grey, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.is_correct === true ? '✅' : o.is_correct === false ? '❌' : '•'} {prettyName(o.section)}: {(o.question_text || '').slice(0, 50)}
                      </span>
                      <span style={{ color: C.navy, whiteSpace: 'nowrap' }}>
                        <strong>{o.answer || '—'}</strong> <span style={{ color: C.grey }}>/ {o.correct_answer}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {(markResponses.writing || []).map((w, i) => (
                <div key={'w'+i} style={{ padding: 14, borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.teal, textTransform: 'uppercase', marginBottom: 6 }}>Writing response {i + 1}</div>
                  <div style={{ fontSize: 13, color: C.navy, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{w.question_text}</div>
                  {w.image_url && <img src={w.image_url} alt="Task chart" style={{ maxWidth: '100%', borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 8 }} />}
                  <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 12, fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {w.answer ? w.answer : <span style={{ color: C.error }}>— No response written —</span>}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: C.grey, marginTop: 4 }}>{w.word_count} words</div>
                </div>
              ))}
              {(markResponses.speaking || []).length > 0 && (
                <div style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, textTransform: 'uppercase', marginBottom: 6 }}>🎤 Speaking prompts (conducted live)</div>
                  {(markResponses.speaking || []).map((s, i) => (
                    <div key={'s'+i} style={{ fontSize: 12, color: C.navy, whiteSpace: 'pre-wrap', padding: '4px 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>{s.question_text}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {markResponses === null && markModal.status && (
            <div style={{ fontSize: 12, color: C.grey, marginBottom: 12 }}>Loading student's responses…</div>
          )}

          {isIelts(markModal) ? (
            <>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: C.grey }}>Listening & Reading are auto-scored. Read the responses above, then enter the <strong>Writing</strong> and <strong>Speaking</strong> bands (0–9); the overall band is the average, rounded to the nearest 0.5.</p>
              <Field label="Writing band (0 – 9)">
                <input type="number" min="0" max="9" step="0.5" value={writingScore}
                  onChange={e => setWritingScore(e.target.value)} placeholder="e.g. 6.5" style={inputStyle} />
              </Field>
              <Field label="Speaking band (0 – 9)">
                <input type="number" min="0" max="9" step="0.5" value={speakingScore}
                  onChange={e => setSpeakingScore(e.target.value)} placeholder="e.g. 7.0" style={inputStyle} />
              </Field>
              {ieltsOverall() != null && (
                <div style={{ background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 8, padding: '10px 14px', margin: '4px 0 12px', fontWeight: 800, color: '#065F46' }}>
                  Overall band: {ieltsOverall().toFixed(1)}
                </div>
              )}
            </>
          ) : (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: C.grey }}>
              This test is fully auto-scored — no manual marking needed. Confirming will finalise the result above and issue the certificate.
            </p>
          )}

          {/* Examiner notes — recorded during the live Speaking test, shown to the student as feedback */}
          <Field label="Examiner notes / feedback (optional)">
            <textarea value={examNotes} onChange={e => setExamNotes(e.target.value)} rows={3}
              placeholder="e.g. Fluent but hesitant on Part 2; strong vocabulary; minor grammar slips with past tenses. Work on linking words."
              style={{ ...inputStyle, resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>Shown to the student on their results as examiner feedback.</div>
          </Field>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap' }}>
            <GhostBtn onClick={printReview}>🖨 Print full review</GhostBtn>
            <div style={{ display: 'flex', gap: 10 }}>
              <GhostBtn onClick={() => setMarkModal(null)}>Cancel</GhostBtn>
              <Btn onClick={submitMark} disabled={markLoading}>
                {markLoading ? 'Saving…' : (isIelts(markModal) ? 'Save Marks' : 'Confirm & Issue Certificate')}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const WalkInTab = ({ testTypes }) => {
  const btn = (bg) => ({ padding:'8px 14px', borderRadius:8, background:bg, color:'#fff', border:'none', fontWeight:600, fontSize:13, cursor:'pointer' });
  const inp = { padding:'10px 12px', borderRadius:8, border:'1px solid #E5E7EB', fontSize:14 };
  const [links, setLinks] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ test_type_id:'', capacity:30, label:'', access_code:'' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/mock-tests/guest-links')
      .then(r => setLinks(r.data?.links || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/admin/tutors').then(r => setTutors((r.data?.tutors) || r.data || [])).catch(() => {}); }, []);
  const assignTutor = async (l, tutorId) => {
    try { await api.patch(`/api/admin/mock-tests/guest-links/${l.id}`, { tutor_id: tutorId || null }); toast.success(tutorId ? 'Tutor assigned' : 'Tutor cleared'); load(); }
    catch { toast.error('Could not assign tutor'); }
  };
  useEffect(() => { if (!form.test_type_id && testTypes.length) setForm(f => ({ ...f, test_type_id:String(testTypes[0].id) })); }, [testTypes, form.test_type_id]);

  const linkUrl = (t) => `${window.location.origin}/mock/${t}`;
  const copy = (t) => navigator.clipboard.writeText(linkUrl(t)).then(() => toast.success('Link copied'));

  const create = async () => {
    if (!form.test_type_id) { toast.error('Pick a test type'); return; }
    setCreating(true);
    try {
      await api.post('/api/admin/mock-tests/guest-links', {
        test_type_id:Number(form.test_type_id), capacity:Number(form.capacity)||30,
        label:form.label, access_code:form.access_code,
      });
      toast.success('Link created'); setForm(f => ({ ...f, label:'', access_code:'' })); load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not create link'); }
    finally { setCreating(false); }
  };
  const toggleActive = async (l) => { try { await api.patch(`/api/admin/mock-tests/guest-links/${l.id}`, { active: !l.active }); load(); } catch { toast.error('Update failed'); } };
  const startAll = async (l) => { if (!window.confirm('Start the test for everyone who has joined now? Latecomers can still join (with the access code if set) and will start automatically. Use Close when the sitting is over.')) return; try { const r = await api.post(`/api/admin/mock-tests/guest-links/${l.id}/start-all`); toast.success(`Started ${r.data.released} candidate(s) · latecomers will auto-start`); load(); } catch { toast.error('Could not start'); } };
  const removeLink = async (l) => { if (!window.confirm('Delete this link? It stops working immediately. Completed results are kept in the Results tab.')) return; try { await api.delete(`/api/admin/mock-tests/guest-links/${l.id}`); toast.success('Link deleted'); load(); } catch { toast.error('Could not delete'); } };

  return (
    <div>
      <div style={{ background:'#F0F9FF', border:'1px solid #BAE6FD', borderRadius:12, padding:16, marginBottom:20, fontSize:13.5, color:'#075985' }}>
        Share one link so anyone can take a mock test <strong>free, with no account</strong>. They enter just their name and wait; you press <strong>Start all</strong> to begin. <strong>Latecomers can still join and will start automatically</strong> — set an <strong>access code</strong> to keep outsiders out. Press <strong>Close</strong> when the sitting is over to stop new joins. Results land in the Results tab &amp; Live Monitor, where you can mark them and issue/share certificates. <strong>Delete</strong> removes a link entirely (completed results are kept).
      </div>
      <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:18, marginBottom:24 }}>
        <h3 style={{ margin:'0 0 14px', fontSize:15 }}>Create a walk-in link</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, alignItems:'end' }}>
          <label style={{ fontSize:12, fontWeight:600, color:'#475569' }}>Test type
            <select value={form.test_type_id} onChange={e=>setForm({...form, test_type_id:e.target.value})} style={{...inp, width:'100%', marginTop:5}}>
              {testTypes.map(tt => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize:12, fontWeight:600, color:'#475569' }}>Capacity
            <input type="number" value={form.capacity} onChange={e=>setForm({...form, capacity:e.target.value})} style={{...inp, width:'100%', marginTop:5}} />
          </label>
          <label style={{ fontSize:12, fontWeight:600, color:'#475569' }}>Label (optional)
            <input value={form.label} onChange={e=>setForm({...form, label:e.target.value})} placeholder="e.g. June walk-in" style={{...inp, width:'100%', marginTop:5}} />
          </label>
          <label style={{ fontSize:12, fontWeight:600, color:'#475569' }}>Access code (optional)
            <input value={form.access_code} onChange={e=>setForm({...form, access_code:e.target.value})} placeholder="blank = open" style={{...inp, width:'100%', marginTop:5}} />
          </label>
          <button onClick={create} disabled={creating} style={{ padding:'11px 18px', borderRadius:10, background:'#00B4D8', color:'#fff', border:'none', fontWeight:700, cursor:'pointer' }}>{creating?'Creating…':'Create link'}</button>
        </div>
      </div>
      {loading ? <p style={{color:'#64748B'}}>Loading…</p> : links.length === 0 ? (
        <p style={{ color:'#64748B' }}>No walk-in links yet. Create one above and share it.</p>
      ) : (
        <div style={{ display:'grid', gap:12 }}>
          {links.map(l => (
            <div key={l.id} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:16, opacity:l.active?1:0.6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:10, alignItems:'center' }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:700, color:'#0F172A' }}>{l.test_type}{l.label ? ` — ${l.label}` : ''} {!l.active && <span style={{fontSize:11,color:'#DC2626'}}>(closed)</span>}</div>
                  <div style={{ fontSize:12.5, color:'#64748B', margin:'2px 0' }}>Seats used: <strong>{l.seats_used}/{l.capacity}</strong>{l.access_code ? ` · code: ${l.access_code}` : ' · open (no code)'}</div>
                  <code style={{ fontSize:12.5, color:'#0369A1', wordBreak:'break-all' }}>{linkUrl(l.token)}</code>
                  <div style={{ fontSize:12.5, color:'#64748B', marginTop:6, display:'flex', alignItems:'center', gap:6 }}>
                    <span>Tutor:</span>
                    <select value={(tutors.find(t => t.user_id === l.assigned_tutor_id)?.id) || ''} onChange={e => assignTutor(l, e.target.value)}
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #E5E7EB', fontSize:12.5 }}>
                      <option value="">Unassigned</option>
                      {tutors.map(t => <option key={t.id} value={t.id}>{t.full_name || `${t.first_name||''} ${t.last_name||''}`.trim() || 'Tutor'}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={()=>copy(l.token)} style={btn('#0EA5E9')}>Copy link</button>
                  <button onClick={()=>startAll(l)} style={btn('#10B981')}>Start all</button>
                  <button onClick={()=>toggleActive(l)} style={btn(l.active?'#64748B':'#10B981')}>{l.active?'Close':'Reopen'}</button>
                  <button onClick={()=>removeLink(l)} style={btn('#DC2626')}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TABS = [
  { key: 'bookings', label: 'Bookings' },
  { key: 'live',     label: '🔴 Live Monitor' },
  { key: 'slots',    label: 'Slots'    },
  { key: 'pricing',  label: 'Pricing'  },
  { key: 'results',  label: 'Results'  },
  { key: 'walkin',   label: '🔗 Walk-in Links' },
];

const fmtMMSS = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const LIVE_STATUS = {
  not_started: { label: 'Waiting to start', color: C.grey,    bg: '#F3F4F6' },
  released:    { label: 'Released',         color: '#1D4ED8', bg: '#DBEAFE' },
  in_progress: { label: 'In progress',      color: C.amber,   bg: '#FEF3C7' },
  paused:      { label: 'Paused',           color: C.error,   bg: '#FEE2E2' },
};

const LiveTab = () => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const tick = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/mock-tests/live');
      setRows(Array.isArray(data) ? data : []);
    } catch {}
    setLoaded(true);
  }, []);

  useEffect(() => {
    let alive = true;
    const run = async () => { if (alive) await tick(); };
    run();
    const id = setInterval(run, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [tick]);

  const act = async (sid, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.post(`/api/admin/mock-tests/sessions/${sid}/${action}`);
      toast.success('Done');
      tick();
    } catch (e) { toast.error(e?.response?.data?.error || 'Action failed'); }
  };

  const ABtn = ({ label, color, onClick }) => (
    <button onClick={onClick} style={{ padding: '5px 12px', borderRadius: 6, background: color, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, marginLeft: 4 }}>{label}</button>
  );

  if (!loaded) return <div style={{ color: C.grey, padding: 24 }}>Loading…</div>;
  if (rows.length === 0) return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 40, textAlign: 'center', color: C.grey }}>
      No active mock tests right now. Activated tests appear here so you can release, pause, or cancel them. Refreshes every 5s.
    </div>
  );
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', fontSize: 12, color: C.grey, borderBottom: `1px solid ${C.border}` }}>
        Live — {rows.length} active · auto-refresh 5s
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: C.grey, fontSize: 12 }}>
            <th style={{ padding: '10px 16px' }}>Candidate</th>
            <th style={{ padding: '10px 16px' }}>Test</th>
            <th style={{ padding: '10px 16px' }}>Status</th>
            <th style={{ padding: '10px 16px' }}>Progress</th>
            <th style={{ padding: '10px 16px' }}>Time left</th>
            <th style={{ padding: '10px 16px' }}>Integrity</th>
            <th style={{ padding: '10px 16px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const st = LIVE_STATUS[r.status] || { label: r.status, color: C.grey, bg: '#F3F4F6' };
            return (
            <tr key={r.session_id} style={{ borderTop: `1px solid ${C.border}`, background: r.flagged ? '#FEF2F2' : '#fff' }}>
              <td style={{ padding: '10px 16px', fontWeight: 600, color: C.navy }}>{r.student_name}</td>
              <td style={{ padding: '10px 16px', color: C.grey }}>{r.test_type}</td>
              <td style={{ padding: '10px 16px' }}><span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span></td>
              <td style={{ padding: '10px 16px' }}>{r.answered}/{r.total}</td>
              <td style={{ padding: '10px 16px', color: r.remaining_seconds < 300 ? C.error : C.navy }}>{r.status === 'in_progress' ? fmtMMSS(r.remaining_seconds) : '—'}</td>
              <td style={{ padding: '10px 16px' }}>
                {r.flagged
                  ? <span style={{ color: C.error, fontWeight: 700 }}>⚠ {r.tab_switches} tab switches</span>
                  : <span style={{ color: C.success }}>OK{r.tab_switches ? ` (${r.tab_switches})` : ''}</span>}
              </td>
              <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                {r.status === 'not_started' && <ABtn label="▶ Start" color={C.success} onClick={() => act(r.session_id, 'release')} />}
                {r.status === 'released' && <span style={{ fontSize: 12, color: C.grey }}>Awaiting candidate</span>}
                {r.status === 'in_progress' && <ABtn label="⏸ Pause" color={C.amber} onClick={() => act(r.session_id, 'pause')} />}
                {r.status === 'paused' && <ABtn label="▶ Resume" color={C.success} onClick={() => act(r.session_id, 'resume')} />}
                {r.status !== 'not_started' && <ABtn label="✕ Cancel" color={C.error} onClick={() => act(r.session_id, 'cancel', 'Cancel this test? The candidate will not be able to continue.')} />}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AdminMockTests = () => {
  const [settings,  setSettings]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [certSlug,  setCertSlug]  = useState('ielts');
  const [certBusy,  setCertBusy]  = useState(false);
  const [paperBusy, setPaperBusy] = useState(false);
  const [prOpen, setPrOpen] = useState(false);
  const [prBusy, setPrBusy] = useState(false);
  const [pr, setPr] = useState({ test_type_id: '', student_name: '', examiner_notes: '', listening: '', reading: '', writing: '', speaking: '', overall_percent: '' });

  const openPaperResult = () => {
    const types = settings?.test_types || [];
    const tt = types.find(t => (t.slug || '').toLowerCase() === certSlug) || types[0];
    setPr({ test_type_id: tt ? String(tt.id) : '', student_name: '', examiner_notes: '', listening: '', reading: '', writing: '', speaking: '', overall_percent: '' });
    setPrOpen(true);
  };
  const submitPaperResult = async () => {
    if (!pr.student_name.trim()) { toast.error('Enter the candidate name'); return; }
    const tt = (settings?.test_types || []).find(t => String(t.id) === String(pr.test_type_id));
    const isI = (tt?.slug || '').toLowerCase() === 'ielts';
    const scores = isI
      ? { listening: pr.listening, reading: pr.reading, writing: pr.writing, speaking: pr.speaking }
      : { overall_percent: pr.overall_percent };
    setPrBusy(true);
    try {
      await api.post('/api/admin/mock-tests/paper-result', { test_type_id: Number(pr.test_type_id), student_name: pr.student_name.trim(), examiner_notes: pr.examiner_notes, scores });
      toast.success('Paper result saved & certificate issued — find it in Results');
      setPrOpen(false);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not save the result'); }
    finally { setPrBusy(false); }
  };

  const printPaper = async () => {
    const tt = (settings?.test_types || []).find(t => (t.slug || '').toLowerCase() === certSlug);
    if (!tt) { toast.error('Pick a valid test type first'); return; }
    setPaperBusy(true);
    try {
      const { data } = await api.get('/api/admin/mock-tests/paper?test_type_id=' + tt.id);
      const w = window.open('', '_blank');
      if (!w) { toast.error('Allow pop-ups to open the printable paper'); return; }
      w.document.open(); w.document.write(buildPaperHtml(data)); w.document.close();
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not generate the paper'); }
    finally { setPaperBusy(false); }
  };

  const toggleListeningScript = async (hide) => {
    try {
      await api.post('/api/admin/mock-tests/listening-script', { hide });
      setSettings(s => ({ ...s, hide_listening_script: hide }));
      toast.success(hide ? 'Listening: audio only — script hidden from students' : 'Listening: script shown to students');
    } catch { toast.error('Could not update the setting'); }
  };

  const previewMockCert = async () => {
    setCertBusy(true);
    try {
      const r = await api.get('/api/admin/mock-tests/certificate/preview?slug=' + certSlug, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not generate certificate preview'); }
    finally { setCertBusy(false); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/api/admin/mock-tests/settings');
        setSettings(data);
      } catch {
        toast.error('Failed to load mock test settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Layout role="tenant_admin">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* Page header */}
        <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: C.navy }}>
              Mock Tests
            </h1>
            <p style={{ margin: '6px 0 0', color: C.grey, fontSize: 15 }}>
              Manage bookings, time slots, pricing, and student results.
            </p>
            {settings?.enabled && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: C.navy, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!settings.hide_listening_script} onChange={e => toggleListeningScript(e.target.checked)} style={{ width: 16, height: 16 }} />
                🎧 Listening: play audio only — hide the script from students (exam mode)
              </label>
            )}
          </div>
          {settings?.enabled && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select value={certSlug} onChange={e => setCertSlug(e.target.value)}
                style={{ padding: '9px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 }}>
                <option value="ielts">IELTS</option>
                <option value="sat">SAT</option>
                <option value="presidential">Presidential</option>
                <option value="cefr">CEFR</option>
              </select>
              <button onClick={printPaper} disabled={paperBusy}
                style={{ padding: '9px 16px', borderRadius: 8, background: '#fff', color: C.navy, border: `2px solid ${C.navy}`, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {paperBusy ? 'Building…' : '🖨 Print Paper'}
              </button>
              <button onClick={openPaperResult}
                style={{ padding: '9px 16px', borderRadius: 8, background: C.teal, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ➕ Paper result
              </button>
              <button onClick={previewMockCert} disabled={certBusy}
                style={{ padding: '9px 16px', borderRadius: 8, background: '#fff', color: C.teal, border: `2px solid ${C.teal}`, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {certBusy ? 'Generating…' : '👁 Preview Certificate'}
              </button>
            </div>
          )}
        </div>

        {prOpen && (() => {
          const tt = (settings?.test_types || []).find(t => String(t.id) === String(pr.test_type_id));
          const isI = (tt?.slug || '').toLowerCase() === 'ielts';
          const inpS = { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box', marginTop: 5 };
          const lblS = { display: 'block', fontSize: 12, fontWeight: 600, color: C.navy, marginTop: 12 };
          return (
            <div onClick={() => setPrOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ margin: '0 0 4px', color: C.navy, fontSize: 18 }}>Record paper test result</h3>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: C.grey }}>Enter the hand-marked scores. A certificate is generated automatically and appears in Results.</p>
                <label style={lblS}>Test type
                  <select value={pr.test_type_id} onChange={e => setPr({ ...pr, test_type_id: e.target.value })} style={inpS}>
                    {(settings?.test_types || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <label style={lblS}>Candidate name (printed on certificate)
                  <input value={pr.student_name} onChange={e => setPr({ ...pr, student_name: e.target.value })} placeholder="e.g. Aziz Karimov" style={inpS} />
                </label>
                {isI ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
                    {['listening', 'reading', 'writing', 'speaking'].map(k => (
                      <label key={k} style={{ ...lblS, textTransform: 'capitalize' }}>{k} band
                        <input type="number" step="0.5" min="0" max="9" value={pr[k]} onChange={e => setPr({ ...pr, [k]: e.target.value })} placeholder="0–9" style={inpS} />
                      </label>
                    ))}
                  </div>
                ) : (
                  <label style={lblS}>Overall score (%)
                    <input type="number" min="0" max="100" value={pr.overall_percent} onChange={e => setPr({ ...pr, overall_percent: e.target.value })} placeholder="0–100" style={inpS} />
                  </label>
                )}
                <label style={lblS}>Examiner notes (optional)
                  <textarea value={pr.examiner_notes} onChange={e => setPr({ ...pr, examiner_notes: e.target.value })} rows={3} placeholder="Feedback for the candidate / marking notes" style={{ ...inpS, resize: 'vertical' }} />
                </label>
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button onClick={() => setPrOpen(false)} style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#fff', color: C.grey, border: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={submitPaperResult} disabled={prBusy} style={{ flex: 2, padding: '11px', borderRadius: 8, background: prBusy ? C.grey : C.teal, color: '#fff', border: 'none', fontWeight: 700, fontSize: 14, cursor: prBusy ? 'default' : 'pointer' }}>{prBusy ? 'Saving…' : 'Save & issue certificate'}</button>
                </div>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: 200, color: C.grey, fontSize: 16,
          }}>
            Loading…
          </div>
        ) : !settings?.enabled ? (
          /* ── Locked state ── */
          <div style={{
            background: '#fff',
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: '56px 32px',
            textAlign: 'center',
            maxWidth: 560,
            margin: '0 auto',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700, color: C.grey }}>
              Mock Tests Not Enabled
            </h2>
            <p style={{ margin: 0, color: C.grey, fontSize: 15, lineHeight: 1.6 }}>
              Mock Tests module is not enabled for your school.
              Contact your <strong style={{ color: C.navy }}>SpeakIQ administrator</strong> to activate this feature.
            </p>
          </div>
        ) : (
          <>
            {/* ── Tab bar ── */}
            <div style={{
              display: 'flex', gap: 4, borderBottom: `2px solid ${C.border}`,
              marginBottom: 28, flexWrap: 'wrap',
            }}>
              {TABS.map(tab => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '10px 22px',
                      border: 'none',
                      borderBottom: active ? `3px solid ${C.teal}` : '3px solid transparent',
                      background: 'none',
                      color: active ? C.teal : C.grey,
                      fontSize: 15,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      marginBottom: -2,
                      transition: 'color .15s',
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* ── Tab content ── */}
            {activeTab === 'bookings' && <BookingsTab />}
            {activeTab === 'slots'    && <SlotsTab    testTypes={settings.test_types || []} />}
            {activeTab === 'pricing'  && <PricingTab  testTypes={settings.test_types || []} />}
            {activeTab === 'live'     && <MockLiveMonitor showInvigilator={true} />}
            {activeTab === 'results'  && <ResultsTab  />}
            {activeTab === 'walkin'   && <WalkInTab   testTypes={settings.test_types || []} />}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminMockTests;
